import { Link } from "react-router-dom";
import { Award, BookOpen, Mic, ScrollText, GraduationCap, Globe, Newspaper, Users, Download, ArrowRight } from "lucide-react";
import { TESTIMONIALS } from "../data/site";
import { useRegion } from "../context/RegionContext";
import Seo from "../components/Seo";

export default function About() {
  const { region } = useRegion();
  const credentials = [
    { icon: ScrollText, label: "Author, Queensland Social Impact Assessment Guideline (2018)" },
    { icon: BookOpen, label: "Author, A Practical Guide to Social Impact Assessment for the Real World" },
    { icon: GraduationCap, label: "Recognised social scientist across government, industry and community sectors" },
    { icon: Users, label: "Led or reviewed 40+ major SIAs across renewables, resources and infrastructure" },
    { icon: Globe, label: "International advisory experience across Australia and the United States" },
    { icon: Mic, label: "Host, Voices of Change - Stories That Matter podcast" },
    { icon: Newspaper, label: "Regular publications, media coverage and conference presentations" },
    { icon: Award, label: "ROAR Woman of the Year (Bronze, 2025)" },
  ];

  return (
    <div data-testid="about-page">
      <Seo
        path="/about"
        title="About Andrea Kanaris"
        description="Andrea Kanaris - social impact practitioner, founder of All Things Social Impact. Advisory work across mining, energy and infrastructure in Australia and the United States."
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Person",
          name: "Andrea Kanaris",
          jobTitle: "Founder, Strategic Advisor",
          worksFor: { "@type": "Organization", name: "All Things Social Impact" },
          url: "https://allthingssocialimpact.com/about",
        }}
      />
      <section className="relative bg-brand-blue text-white py-24 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 opacity-25">
          <img src="https://images.unsplash.com/photo-1611564393101-b834b74a9b48?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000" alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8">
            <div className="eyebrow text-brand-yellow mb-6">About the practice</div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mb-6">All Things Social Impact is a specialist social impact and social risk advisory practice led by Andrea Kanaris.</h1>
            <p className="text-lg lg:text-xl text-white/85 max-w-3xl leading-relaxed">
              Andrea is a senior advisor, independent reviewer and expert witness with more than 20 years' experience helping organisations identify social risk, improve project decisions and deliver stronger community outcomes.
            </p>
          </div>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5">
            <img src="/andrea-kanaris.jpg" alt="Andrea Kanaris - Founder & Principal, All Things Social Impact" className="w-full aspect-[4/5] object-cover sticky top-28" />
          </div>
          <div className="lg:col-span-7">
            <div className="eyebrow mb-4">Biography</div>
            <h2 className="font-serif text-3xl lg:text-4xl text-brand-blue leading-tight mb-6">Trusted where the decisions are hardest, and the scrutiny is highest.</h2>
            <div className="space-y-5 text-slate-600 leading-relaxed">
              <p>Andrea has spent more than two decades helping executives, project directors, government agencies and lender-side teams work through the social risks that decide whether major projects proceed, stall or lose licence. She is retained as a senior advisor, independent peer reviewer and expert witness across renewable energy, transmission, mining, critical minerals, water, infrastructure and land development.</p>
              <p>She authored Queensland's Social Impact Assessment Guideline in 2018, has led or independently reviewed more than 40 major social impact assessments, and is regularly called on when a project needs rigorous social science, credible community outcomes and defensible advice under regulatory or board-level scrutiny.</p>
              <p>Andrea works with clients personally. Every engagement is led by her, supported by a curated network of specialist collaborators, so decision-makers always deal with senior judgement, not layered account teams. Her focus is practical: identify the risks that matter, sharpen the decision, and deliver stronger outcomes for communities and proponents alike.</p>
              <p>She is the author of <em>A Practical Guide to Social Impact Assessment for the Real World</em>, host of the <em>Voices of Change - Stories That Matter</em> podcast, and a frequent conference speaker across Australia and internationally.</p>
            </div>

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {credentials.map(({ icon: Icon, label }) => (
                <div key={label} className="flex items-start gap-3 border border-slate-200 p-5">
                  <Icon className="w-5 h-5 text-brand-orange flex-shrink-0 mt-1" />
                  <div className="text-sm text-slate-700 leading-relaxed">{label}</div>
                </div>
              ))}
            </div>

            <div className="mt-10 flex flex-wrap gap-4">
              <a href={`${process.env.REACT_APP_BACKEND_URL}/api/capability-statement.pdf?region=${region}`} target="_blank" rel="noreferrer" data-testid="download-capability-btn" className="btn-primary"><Download className="w-4 h-4" /> Download Capability Statement (PDF)</a>
              <Link to="/contact" className="btn-secondary">Contact Us <ArrowRight className="w-4 h-4" /></Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-slate-50">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="mb-14 max-w-2xl">
            <div className="eyebrow mb-4">Approach</div>
            <h2 className="font-serif text-3xl lg:text-4xl text-brand-blue leading-tight">How we work.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              { n: "01", t: "Evidence first", b: "Every recommendation stands on baseline data, primary research and comparative practice. We do not guess." },
              { n: "02", t: "Senior attention", b: "Andrea leads every engagement personally. No junior reshuffles, no diluted advice." },
              { n: "03", t: "Practical judgement", b: "We tell you what to do - with clarity about what will and will not work in your context." },
            ].map((p) => (
              <div key={p.n} className="bg-white border border-slate-200 p-8">
                <div className="text-brand-orange font-serif font-bold text-3xl mb-4">{p.n}</div>
                <h3 className="font-serif text-xl text-brand-blue mb-3">{p.t}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{p.b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="testimonials" className="py-24 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="mb-14 max-w-2xl">
            <div className="eyebrow mb-4">Testimonials</div>
            <h2 className="font-serif text-3xl lg:text-4xl text-brand-blue leading-tight">In their words.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {TESTIMONIALS.map((t) => (
              <div key={t.name} data-testid={`testimonial-${t.name.toLowerCase().replace(/\s+/g, "-")}`} className="border border-slate-200 p-8 flex flex-col">
                <blockquote className="font-serif text-lg text-brand-blue leading-relaxed mb-6 flex-1">"{t.quote}"</blockquote>
                <div className="border-t border-slate-200 pt-4">
                  <div className="font-semibold text-slate-900 text-sm">{t.name}</div>
                  <div className="text-xs text-slate-500 uppercase tracking-widest mt-1">{t.title}</div>
                  {t.project && <div className="text-xs text-brand-orange font-semibold uppercase tracking-widest mt-2">{t.project}</div>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
